import React from 'react'
import { PieChart, Pie, Tooltip, Cell } from 'recharts'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import BodyWidget from '@SonarCX/components/atoms/BodyWidget'
import LoadingSpinner from '@SonarCX/components/atoms/LoadingSpinner'

import { getStatusColor, getStatusName } from './helpers'
import { selectTaskStatus, fetchStatus } from '@SonarCX/slices/taskStatus'
import { useStats } from '@SonarCX/providers/StatsProvider'
import useStaticChartPosition from '../hooks/useStaticChartPosition'
import useStyles from './styles'

type TaskStatusesProps = {
  mineOnly?: boolean
}

const TaskStatuses: React.FC<TaskStatusesProps> = ({ mineOnly }) => {
  const dispatch = useAppDispatch()

  const {
    statuses: taskStatuses,
    loaded: taskStatusLoaded,
    loading: taskStatusLoading
  } = useAppSelector(selectTaskStatus)

  const { taskStats, taskLoaded, globalTasks, globalLoaded } = useStats()
  const { containerRef, chartRef } = useStaticChartPosition()

  const loaded = React.useMemo(
    () => (mineOnly ? taskLoaded : globalLoaded),
    [mineOnly, taskLoaded, globalLoaded]
  )

  const stats = React.useMemo(
    () => (mineOnly ? taskStats : globalTasks),
    [mineOnly, taskStats, globalTasks]
  )

  const formatted = React.useMemo(() => {
    if (!stats) {
      return []
    }

    const newStats = [...stats.statuses]

    if (!newStats.length) {
      return []
    }

    return newStats.map(stat => {
      const { id, value } = stat
      const name = getStatusName(taskStatuses, id)
      const color = getStatusColor(taskStatuses, id)

      return {
        id,
        value,
        name,
        color
      }
    })
  }, [stats, taskStatuses])

  const empty = React.useMemo(() => (formatted.length ?? 0) === 0, [formatted])

  const classes = useStyles()

  React.useEffect(() => {
    if (!taskStatusLoaded && !taskStatusLoading) {
      dispatch(fetchStatus())
    }
  }, [taskStatusLoaded, taskStatusLoading, dispatch])

  return (
    <BodyWidget
      ref={containerRef}
      title={`${mineOnly ? 'My' : 'All'} Tasks by Status`}
    >
      <div className={classes.statsContainer}>
        {!loaded && (
          <div style={{ position: 'relative', flex: '1' }}>
            <LoadingSpinner />
          </div>
        )}

        {loaded && empty && <div className={classes.emptyTitle}>No tasks</div>}

        {loaded && !empty && (
          <>
            <div className={classes.header}>
              <div>
                {formatted.map(status => (
                  <div key={status.id} className={classes.statusInfo}>
                    <div
                      className={classes.dot}
                      style={{
                        marginRight: '1rem',
                        backgroundColor: status.color
                      }}
                    />
                    {status.name}: {status.value}
                  </div>
                ))}
              </div>
            </div>

            <div className={classes.chartContainer}>
              <div ref={chartRef} className={classes.pieChart}>
                <PieChart width={207} height={200}>
                  <Pie
                    data={formatted}
                    innerRadius={35}
                    outerRadius={75}
                    fill='#8884d8'
                    paddingAngle={5}
                    dataKey='value'
                  >
                    {formatted.map(status => (
                      <Cell key={`cell-${status.id}`} fill={status.color} />
                    ))}
                  </Pie>

                  <Tooltip wrapperStyle={{ outline: 'none' }} />
                </PieChart>
              </div>
            </div>
          </>
        )}
      </div>
    </BodyWidget>
  )
}

export default TaskStatuses
